import { useState, useEffect } from 'react'
import { ChevronDown, ChevronRight, Clock, Activity, RefreshCw, Filter } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { useEventsLog, type EventLogEntry } from '@/entities/eventLog'

// Primary events (shown in default filter)
const DEFAULT_EVENT_TYPES = new Set([
  'user_speech_started',
  'user_speech_ended',
  'agent_speech_started',
  'agent_speech_ended',
  'metrics_turn_latency',
  'llm_request',
  'llm_response',
  'tool_call',
  'tool_response',
  'conversation_started',
  'timeout_ended_call',
  'conversation_item_added',
])

// Event type to category mapping for color coding
const eventCategoryMap: Record<string, { color: string; bgColor: string; emoji: string }> = {
  // === PRIMARY EVENTS (default filter) ===
  // Speech events
  user_speech_started: { color: 'text-orange-600', bgColor: 'bg-orange-50', emoji: '🟠' },
  user_speech_ended: { color: 'text-orange-600', bgColor: 'bg-orange-50', emoji: '🟠' },
  agent_speech_started: { color: 'text-orange-600', bgColor: 'bg-orange-50', emoji: '🟠' },
  agent_speech_ended: { color: 'text-orange-600', bgColor: 'bg-orange-50', emoji: '🟠' },

  // LLM events
  llm_request: { color: 'text-yellow-600', bgColor: 'bg-yellow-50', emoji: '🟡' },
  llm_response: { color: 'text-yellow-600', bgColor: 'bg-yellow-50', emoji: '🟡' },

  // Tool events
  tool_call: { color: 'text-green-600', bgColor: 'bg-green-50', emoji: '🟢' },
  tool_response: { color: 'text-green-600', bgColor: 'bg-green-50', emoji: '🟢' },

  // Conversation lifecycle
  conversation_started: { color: 'text-blue-600', bgColor: 'bg-blue-50', emoji: '🔵' },
  timeout_ended_call: { color: 'text-red-600', bgColor: 'bg-red-50', emoji: '🔴' },
  conversation_item_added: { color: 'text-indigo-600', bgColor: 'bg-indigo-50', emoji: '💬' },

  // === SECONDARY EVENTS (not in default filter) ===
  // Call lifecycle (internal)
  call_initialized: { color: 'text-blue-400', bgColor: 'bg-blue-50', emoji: '🔵' },
  call_registered: { color: 'text-blue-400', bgColor: 'bg-blue-50', emoji: '🔵' },
  call_ended: { color: 'text-blue-400', bgColor: 'bg-blue-50', emoji: '🔵' },
  call_analyzed: { color: 'text-blue-400', bgColor: 'bg-blue-50', emoji: '🔵' },

  // Turn management (internal)
  turn_started: { color: 'text-purple-400', bgColor: 'bg-purple-50', emoji: '🟣' },
  turn_completed: { color: 'text-purple-400', bgColor: 'bg-purple-50', emoji: '🟣' },
  turn_timeout_started: { color: 'text-purple-400', bgColor: 'bg-purple-50', emoji: '🟣' },
  turn_timeout_stopped: { color: 'text-purple-400', bgColor: 'bg-purple-50', emoji: '🟣' },

  // LLM internals
  llm_framework_context: { color: 'text-yellow-400', bgColor: 'bg-yellow-50', emoji: '🟡' },

  // Tool execution error
  tool_execution_error: { color: 'text-red-400', bgColor: 'bg-red-50', emoji: '🔴' },

  // State changes (verbose)
  user_state_changed: { color: 'text-cyan-400', bgColor: 'bg-cyan-50', emoji: '🔷' },
  agent_state_changed: { color: 'text-cyan-400', bgColor: 'bg-cyan-50', emoji: '🔷' },
  session_state_changed: { color: 'text-cyan-400', bgColor: 'bg-cyan-50', emoji: '🔷' },

  // Metrics
  metrics_collected: { color: 'text-pink-400', bgColor: 'bg-pink-50', emoji: '🟪' },
  metrics_turn_latency: { color: 'text-pink-400', bgColor: 'bg-pink-50', emoji: '🟪' },

  // Errors
  error_recoverable: { color: 'text-red-600', bgColor: 'bg-red-50', emoji: '🔴' },
  error_fatal: { color: 'text-red-600', bgColor: 'bg-red-50', emoji: '🔴' },

  // Default
  default: { color: 'text-gray-600', bgColor: 'bg-gray-50', emoji: '⚪' },
}

function getEventCategory(eventType: string) {
  return eventCategoryMap[eventType] || eventCategoryMap.default
}

interface EventCardProps {
  event: EventLogEntry
}

function EventCard({ event }: EventCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const category = getEventCategory(event.event_type)

  // Filter out call_id from event_data for display
  const displayData = event.event_data
    ? Object.fromEntries(Object.entries(event.event_data).filter(([key]) => key !== 'call_id'))
    : null
  const hasDisplayData = displayData && Object.keys(displayData).length > 0

  return (
    <div className={cn('border rounded-lg p-3 transition-colors', category.bgColor, 'hover:shadow-sm')}>
      <div className="flex items-start gap-2">
        {hasDisplayData ? (
          <span
            className="mt-0.5 cursor-pointer hover:bg-gray-200 rounded p-0.5 -m-0.5"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-500" />
            )}
          </span>
        ) : (
          <span className="mt-0.5 p-0.5 -m-0.5 w-4" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-body">{category.emoji}</span>
            <span className={cn('font-medium text-body', category.color)}>{event.event_type}</span>
            {event.title && <span className="text-detail text-gray-600 truncate">{event.title}</span>}
          </div>
          <div className="flex items-center gap-2 mt-1 text-detail text-gray-500">
            <Clock className="h-3 w-3" />
            <span>{event.timestamp}</span>
            <span className="text-gray-300">|</span>
            <span
              className={cn(
                event.log_level === 'ERROR'
                  ? 'text-red-600'
                  : event.log_level === 'WARNING'
                    ? 'text-yellow-600'
                    : 'text-gray-500'
              )}
            >
              {event.log_level}
            </span>
          </div>
        </div>
      </div>

      {isExpanded && hasDisplayData && (
        <div className="mt-3 ml-6 p-3 bg-white rounded border text-detail" onClick={(e) => e.stopPropagation()}>
          <pre className="whitespace-pre-wrap break-all text-gray-700 font-mono select-text">
            {JSON.stringify(displayData, null, 2)}
          </pre>
        </div>
      )}
    </div>
  )
}

interface CallEventsLogProps {
  callId: string
}

export function CallEventsLog({ callId }: CallEventsLogProps) {
  const { data, isLoading, error, refetch, isError } = useEventsLog(callId)

  // Initialize with default event types filter
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(DEFAULT_EVENT_TYPES)
  const [isDefaultFilter, setIsDefaultFilter] = useState(true)

  // Reset filter to default when callId changes
  useEffect(() => {
    setSelectedTypes(DEFAULT_EVENT_TYPES)
    setIsDefaultFilter(true)
  }, [callId])

  if (isLoading) {
    return (
      <div className="p-4 text-center text-gray-500">
        <Activity className="h-6 w-6 mx-auto mb-2 animate-pulse" />
        Loading event logs...
      </div>
    )
  }

  if (isError) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    // If access denied (not superadmin), just hide the section entirely
    if (errorMessage.includes('Superadmin') || errorMessage.includes('Unauthorized') || errorMessage.includes('403')) {
      return null
    }

    return (
      <div className="p-4 text-center text-gray-500">
        <div className="text-red-500 mb-2">Failed to load event logs</div>
        <div className="text-detail">{errorMessage}</div>
        <Button variant="outline" size="sm" className="mt-2" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-1" />
          Retry
        </Button>
      </div>
    )
  }

  if (data && data.events.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500">
        <Activity className="h-8 w-8 mx-auto mb-2 opacity-30" />
        <p>No event log found for this call</p>
      </div>
    )
  }

  const events = data?.events || []

  // Get unique event types for filter
  const eventTypes = [...new Set(events.map((e) => e.event_type))].sort()

  // Filter events - if no types selected, show all events
  const filteredEvents = selectedTypes.size === 0 ? events : events.filter((e) => selectedTypes.has(e.event_type))

  const toggleType = (type: string) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev)
      if (next.has(type)) {
        next.delete(type)
      } else {
        next.add(type)
      }
      return next
    })
    setIsDefaultFilter(false)
  }

  const selectAll = () => {
    setSelectedTypes(new Set(eventTypes))
    setIsDefaultFilter(false)
  }

  const selectNone = () => {
    setSelectedTypes(new Set())
    setIsDefaultFilter(false)
  }

  const selectDefault = () => {
    setSelectedTypes(DEFAULT_EVENT_TYPES)
    setIsDefaultFilter(true)
  }

  return (
    <>
      <div className="space-y-3">
        <h3 className="font-medium text-heading">Event Logs</h3>
        <div className="p-3 bg-gray-50 rounded-md">
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-gray-500" />
                <span className="text-body font-medium">
                  {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
                </span>
                {selectedTypes.size > 0 && (
                  <span className="text-detail text-gray-500">(filtered from {events.length})</span>
                )}
              </div>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 gap-1">
                    <Filter className="h-3 w-3" />
                    {selectedTypes.size === 0
                      ? 'No filter'
                      : isDefaultFilter
                        ? 'Primary events'
                        : `${selectedTypes.size} type${selectedTypes.size !== 1 ? 's' : ''}`}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-2" align="end">
                  <div className="flex gap-1 mb-2">
                    <Button
                      variant={isDefaultFilter ? 'default' : 'outline'}
                      size="sm"
                      className="h-6 flex-1"
                      onClick={selectDefault}
                    >
                      Default
                    </Button>
                    <Button variant="outline" size="sm" className="h-6 flex-1" onClick={selectAll}>
                      All
                    </Button>
                    <Button variant="outline" size="sm" className="h-6 flex-1" onClick={selectNone}>
                      None
                    </Button>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto space-y-1">
                    {eventTypes.map((type) => {
                      const count = events.filter((e) => e.event_type === type).length
                      const category = getEventCategory(type)
                      return (
                        <label
                          key={type}
                          className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-100 cursor-pointer text-detail"
                        >
                          <Checkbox checked={selectedTypes.has(type)} onCheckedChange={() => toggleType(type)} />
                          <span className="text-body">{category.emoji}</span>
                          <span className="flex-1 truncate">{type}</span>
                          <span className="text-gray-400">({count})</span>
                        </label>
                      )
                    })}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
              {filteredEvents.map((event, index) => (
                <EventCard key={`${event.timestamp}-${index}`} event={event} />
              ))}
            </div>
          </div>
        </div>
      </div>

      <Separator className="my-2" />
    </>
  )
}